import React from 'react';
import { checkTripEnded } from '@/_helper/checkAccess';

function getDurationDays(startDate, endDate) {
    if (startDate !== undefined && endDate !== undefined) {
        let parts = startDate.split('.');
        let dateString = parts[1] + '/' + parts[0] + '/' + parts[2];
        let date1 = new Date(dateString);

        parts = endDate.split('.');
        dateString = parts[1] + '/' + parts[0] + '/' + parts[2];
        let date2 = new Date(dateString);
        let difference = date2.getTime() - date1.getTime();

        return Math.ceil(difference / (1000 * 3600 * 24));
    }
}

export default getDurationDays;

export function tripStartCountdown(startDay) {
    if (startDay) {
        let parts = startDay.split('.');
        let dateString = parts[1] + '/' + parts[0] + '/' + parts[2];
        let endDate = new Date(dateString);
        let today = new Date();
        today.setHours(0);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);
        let oneDay = 24 * 60 * 60 * 1000;
        if (today.getTime() < endDate.getTime()) {
            return Math.round(Math.abs((today.getTime() - endDate.getTime()) / oneDay));
        } else if (today.getTime() === endDate.getTime()) {
            return 0;
        }
    }
    return false;
}

export function getShortWeekday(date, options = { weekday: 'short' }) {
    if (date !== undefined) {
        let parts = date.split('.');
        let dateString = parts[2] + '-' + parts[1] + '-' + parts[0];
        return new Date(dateString).toLocaleString('de-de', options);
    }
}

export function getRegion(hotelDetails) {
    if (hotelDetails && hotelDetails[0] !== undefined) {
        let areaDescription = hotelDetails[0].areaDescription;
        return (
            <span>
                {' '}
                nach <span dangerouslySetInnerHTML={{ __html: areaDescription }}></span>
            </span>
        );
    }
}

export function getFlightStops(f, direction) {
    if (direction === 'outbound' && f.outbound !== undefined) {
        let outboundStops = f.outbound.length - 1;
        return outboundStops > 0 ? `${outboundStops} Stopp${outboundStops > 1 ? 's' : ''}` : 'Direktflug';
    } else if (direction === 'inbound' && f.inbound !== undefined) {
        let inboundStops = f.inbound.length - 1;
        return inboundStops > 0 ? `${inboundStops} Stopp${inboundStops > 1 ? 's' : ''}` : 'Direktflug';
    }
}

export function getHoursAndMinutes(totalSeconds) {
    let totalMinutes = Math.floor(totalSeconds / 60);
    let hours = Math.floor(totalMinutes / 60);
    let minutes = totalMinutes % 60;
    return (hours !== 0 ? hours + 'h ' : '') + minutes + 'm';
}

export function getTravellers(data) {
    if (data !== undefined && data.travellers !== undefined) {
        let travellers = Object.keys(data.travellers).map((item) => {
            return (
                <div className="traveller" key={'travellers' + item}>
                    <div>
                        {data.travellers[item].foreName} {data.travellers[item].surName}
                    </div>
                    <div>{data.travellers[item].birthDate}</div>
                </div>
            );
        });

        return travellers;
    }
}

export function getLuggageDetails(data) {
    if (data !== undefined && data.luggage !== undefined) {
        let luggage = Object.keys(data.luggage).map((item) => {
            let parts = data.luggage[item].split(';');

            return (
                <div key={'luggage' + item}>
                    {parts.map((part, index) => {
                        return (
                            <div className="luggage-item" key={'luggage' + item + 'part' + index}>
                                {part}
                            </div>
                        );
                    })}
                </div>
            );
        });

        return luggage;
    }
}

export function getParticipants(excursion) {
    return excursion.participants.map((p, index) => {
        return <div key={index}>{`${p.firstName} ${p.lastName}`}</div>;
    });
}

export function getDate(excursion) {
    const startDate = `${getShortWeekday(excursion.startDate)}., ${excursion.startDate}`;
    const endDate = `${getShortWeekday(excursion.endDate)}., ${excursion.endDate}`;
    return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
}

/**
 * counts duplicate rooms and removes them
 * @param {array<object>} rooms
 * @returns {{ count: number; meal: string; name: string}[]}
 */
export const mapRooms = (rooms) => {
    const result = {};
    rooms
        .map((item) => {
            return { name: item.room ?? item.name, meal: item.meal };
        })
        .forEach((item) => {
            const key = JSON.stringify(item);
            if (!result[key]) {
                result[key] = { ...item, count: 0 };
            }
            result[key].count++;
        });
    return Object.values(result);
};

export function getFlightAlternativeSearchLink(
    bookingNumber,
    bookingData,
    affiliate_checkIsAllowToBookService,
    storage
) {
    const service = 'flightChangeSearch';

    if (
        !bookingNumber ||
        !bookingData ||
        !storage.get('travelStart') ||
        !storage.get('firstPaxName') ||
        !storage.get('access_token') ||
        (window.$affiliate && !affiliate_checkIsAllowToBookService(service).includes(service)) ||
        bookingData?.data?.allowedInteractions?.login?.reason === 'cruise-apis'
    ) {
        return null;
    }

    const searchParams = new URLSearchParams({
        bookingId: bookingNumber,
        travelStart: storage.get('travelStart'),
        firstPaxName: storage.get('firstPaxName'),
        hash: storage.get('access_token'),
        step: 'flight',
    });

    return window.$searchFrontendUrl + '/search?' + searchParams.toString();
}

/**
 *
 * @param {BookingData} bookingData
 * @returns {'OK'|'XX'|'NO'|'over'}
 * BookingState:
 * OK -> Buchung bestätigt
 * XX -> Buchung storniert
 * NO -> Buchung abgesagt
 * over -> Buchung in der Vergangenheit
 */
export const getBookingState = (bookingData) => {
    const packageState = bookingData?.data?.serviceInformation?.package?.state;
    if (!packageState) {
        return 'NO';
    }

    if ((packageState === 'OK' || packageState === 'MO' || packageState === 'TB') && checkTripEnded()) {
        return 'over';
    } else {
        return packageState === 'MO' || packageState === 'TB' || packageState === 'SB' ? 'OK' : packageState;
    }
};
